import * as THREE from 'three';
import CameraControls from 'camera-controls';
import Application from '../Application';
import EventEmitter from '../Utils/EventEmitter';
import Sizes from '../Utils/Sizes';
import Time from '../Utils/Time';

CameraControls.install({ THREE });

export default class Camera extends EventEmitter {
    application: Application;
    sizes: Sizes;
    scene: THREE.Scene;
    time: Time;
    instance!: THREE.PerspectiveCamera;
    controls?: CameraControls;
    private clock = new THREE.Clock();
    private interactionLocked = false;
    private unlockedMouseButtons?: {
        left: number;
        middle: number;
        right: number;
        wheel: number;
    };
    private unlockedTouches?: {
        one: number;
        two: number;
        three: number;
    };

    constructor() {
        super();
        this.application = new Application();
        this.sizes = this.application.sizes;
        this.scene = this.application.scene;
        this.time = this.application.time;

        this.setInstance();
    }

    private setInstance() {
        this.instance = new THREE.PerspectiveCamera(
            45,
            this.sizes.width / this.sizes.height,
            0.1,
            10000
        );
        this.instance.position.set(2.5, 1.6, 3.2);
        this.instance.lookAt(0, 1.0, 0);
        this.scene.add(this.instance);
    }

    createControls() {
        const canvas = this.application.canvas;
        this.controls = new CameraControls(this.instance, canvas);
        this.controls.minDistance = 1.2;
        this.controls.maxDistance = 8;
        this.controls.minPolarAngle = 0.4;
        this.controls.maxPolarAngle = Math.PI / 2 - 0.05;
        this.controls.azimuthRotateSpeed = 0.6;
        this.controls.polarRotateSpeed = 0.6;
        this.controls.dollySpeed = 0.6;
        this.controls.draggingSmoothTime = 0.18;
        this.controls.smoothTime = 0.22;
        this.controls.setTarget(0, 1.0, 0);
        this.unlockedMouseButtons = { ...this.controls.mouseButtons };
        this.unlockedTouches = { ...this.controls.touches };
        canvas.addEventListener('wheel', this.onProtectedWheel, { passive: false });
        canvas.addEventListener('gesturestart', this.onProtectedGesture as EventListener);
        canvas.addEventListener('gesturechange', this.onProtectedGesture as EventListener);
    }

    setInteractionLocked(locked: boolean) {
        if (!this.controls || locked === this.interactionLocked) return;
        this.interactionLocked = locked;

        if (locked) {
            this.controls.cancel();
            this.controls.mouseButtons.left = CameraControls.ACTION.NONE;
            this.controls.mouseButtons.middle = CameraControls.ACTION.NONE;
            this.controls.mouseButtons.right = CameraControls.ACTION.NONE;
            this.controls.mouseButtons.wheel = CameraControls.ACTION.NONE;
            this.controls.touches.one = CameraControls.ACTION.NONE;
            this.controls.touches.two = CameraControls.ACTION.NONE;
            this.controls.touches.three = CameraControls.ACTION.NONE;
            return;
        }

        if (this.unlockedMouseButtons) {
            this.controls.mouseButtons.left = this.unlockedMouseButtons.left;
            this.controls.mouseButtons.middle = this.unlockedMouseButtons.middle;
            this.controls.mouseButtons.right = this.unlockedMouseButtons.right;
            this.controls.mouseButtons.wheel = this.unlockedMouseButtons.wheel;
        }
        if (this.unlockedTouches) {
            this.controls.touches.one = this.unlockedTouches.one;
            this.controls.touches.two = this.unlockedTouches.two;
            this.controls.touches.three = this.unlockedTouches.three;
        }
    }

    private onProtectedWheel = (event: WheelEvent) => {
        if (!this.interactionLocked) return;
        event.preventDefault();
        event.stopPropagation();
    };

    private onProtectedGesture = (event: Event) => {
        if (!this.interactionLocked) return;
        event.preventDefault();
        event.stopPropagation();
    };

    enterMonitor(target: THREE.Vector3, lookAt: THREE.Vector3) {
        if (!this.controls) return;
        const dir = new THREE.Vector3().subVectors(target, lookAt).normalize();
        const cam = lookAt.clone().add(dir.multiplyScalar(0.0001));
        this.controls.setLookAt(cam.x, cam.y, cam.z, lookAt.x, lookAt.y, lookAt.z, true);
    }

    exitMonitor() {
        if (!this.controls) return;
        this.controls.setLookAt(2.5, 1.6, 3.2, 0, 1.0, 0, true);
    }

    resize() {
        this.instance.aspect = this.sizes.width / this.sizes.height;
        this.instance.updateProjectionMatrix();
    }

    update() {
        const delta = this.clock.getDelta();
        if (this.controls) this.controls.update(delta);
    }
}
